(() => {
  const category = (tag, excluded = []) => `tag:"${tag}"${excluded.map(value => ` -tag:"${value}"`).join("")}`;
  const groups = {
    bikini: '(tag:"bikini set" OR tag:"bikini top" OR tag:"bikini bottom")',
    'dresses and skirts': `(${category("dress", ["cover-up", "swim dress"])} OR (${category("skirt", ["cover-up", "swim skirt", "matching set"])}))`,
    'swim skirts and swim dresses': '(tag:"swim skirt" OR tag:"swim dress") -tag:"cover-up"',
    beachwear: '(tag:"swimwear" OR tag:"cover-up")'
  };
  const rules = [
    [/swim skirts? (?:and )?swim dress(?:es)?|swim dress(?:es)? (?:and )?swim skirts?/, groups['swim skirts and swim dresses']],
    [/dress(?:es)? (?:and )?skirts?|skirts? (?:and )?dress(?:es)?/, groups['dresses and skirts']],
    [/(?:bikini |swimsuit |swim |beach )?cover ?ups?|sarongs?|pareos?|cache maillots?|copricostum[ei]/, category("cover-up")],
    [/one pieces?(?: (?:swimsuits?|swimwear|bikinis?))?|onepieces?|maillots? une piece|costum[ei] inter[oi]/, category("one-piece swimsuit")],
    [/swim dress(?:es)?|swimdress(?:es)?/, category("swim dress", ["cover-up"])],
    [/swim skirts?|swim skorts?/, category("swim skirt", ["cover-up"])],
    [/swim shorts?/, category("swim shorts", ["cover-up"])],
    [/swim pants?/, category("swim pants", ["cover-up"])],
    [/bikini tops?/, category("bikini top")],
    [/bikini bottoms?/, category("bikini bottom")],
    [/swim tops?/, category("swim top", ["cover-up", "tankini", "swim set"])],
    [/swim bottoms?/, category("swim bottom", ["cover-up", "tankini", "swim set"])],
    [/tankinis?(?: sets?)?/, category("tankini", ["cover-up"])],
    [/(?:(?:two|2) piece )?bikinis?(?: sets?)?/, groups.bikini],
    [/swim sets?/, category("swim set")],
    [/swimwear|swimsuits?|swim suits?|bathing suits?|maillots? de bain|costum[ei] da bagno/, category("swimwear")],
    [/beachwear|beach wear/, groups.beachwear],
    [/bodysuits?|body suits?|bodies/, category("bodysuit")],
    [/(?:matching|co ord|coord|two piece) sets?|co ords?|coords?/, category("matching set")],
    [/lounge sets?|loungewear/, '(tag:"loungewear" OR tag:"lounge set")'],
    [/pajamas?(?: sets?)?|pyjamas?(?: sets?)?/, category("pajama set")],
    [/jumpsuits?|combinaisons?|tute/, category("jumpsuit")],
    [/rompers?|playsuits?/, category("romper")],
    [/dress(?:es)?|vestit[oi]|abit[oi]/, category("dress", ["cover-up", "swim dress"])],
    [/skirts?|skorts?|jupes?|gonn[ae]/, category("skirt", ["cover-up", "swim skirt", "matching set"])],
    [/pants?|trousers?|jeans|pantalons?|pantaloni/, category("pants", ["cover-up", "swim pants", "matching set"])],
    [/shorts/, category("shorts", ["cover-up", "swim shorts", "matching set"])],
    [/cardigans?/, category("cardigan")],
    [/sweaters?|pullovers?/, category("sweater", ["dress"])],
    [/hoodies?/, category("hoodie")],
    [/sweatshirts?/, category("sweatshirt")],
    [/blazers?/, category("blazer")],
    [/jackets?/, category("jacket")],
    [/coats?/, category("coat")],
    [/outerwear/, category("outerwear")],
    [/robes?/, category("robe")],
    [/blouses?/, category("blouse")],
    [/t shirts?|tees?/, category("t-shirt")],
    [/shirts?/, category("shirt", ["cover-up", "matching set"])],
    [/tank tops?/, category("tank top", ["cover-up", "swim top"])],
    [/camisoles?|camis?/, category("cami", ["cover-up", "swim top"])],
    [/(?:resort )?tops?/, category("top", ["cover-up", "swim top", "matching set"])],
    [/sets?/, category("set")]
  ].map(([pattern, query]) => [new RegExp(`\\b(?:${pattern.source})\\b`, "i"), query]);
  const ignored = new Set(["a", "an", "the", "and", "for", "with", "in", "of", "women", "womens", "woman", "ladies", "womans", "pour", "femme", "femmes", "donna", "per"]);
  const normalize = value => String(value || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[’']/g, "").replace(/[^\p{L}\p{N}]+/gu, " ").trim().replace(/\s+/g, " ");
  const collectionHandle = value => {
    const term = normalize(value);
    if (/^(?:new arrivals?|new in)$/.test(term)) return "new-arrivals";
    if (/^(?:almost gone(?: low stock)?|low stock)$/.test(term)) return "almost-gone";
    if (/^(?:best sellers?|bestsellers?)$/.test(term)) return "best-sellers";
    if (/^flash sales?$/.test(term)) return "flash-sales";
    return null;
  };
  const decode = value => {
    const query = String(value || "");
    if (!/(?:^|[ (])(?:title|tag|variants\.(?:title|sku)):/.test(query)) return query;
    return query.split(" AND ").map(part => {
      const group = Object.entries(groups).find(([, compiled]) => compiled === part);
      if (group) return group[0];
      const phrase = part.match(/(?:title|tag):"([^"]+)"/);
      if (phrase) return phrase[1];
      return part.split(" OR ")[0].replace(/^\(?title:/, "").replace(/[()*"]/g, "").trim();
    }).join(" ");
  };
  function compile(value) {
    let text = normalize(decode(value));
    if (!text) return "";
    let chosen = null;
    const language = typeof document === "undefined" ? "en" : document.documentElement.lang;
    if (/^fr\b/i.test(language) && /\brobes?\b/.test(text)) {
      chosen = category("dress", ["cover-up", "swim dress"]);
      text = text.replace(/\brobes?\b/, " ");
    }
    if (!chosen) {
      for (const [pattern, query] of rules) {
        if (!pattern.test(text)) continue;
        chosen = query;
        text = text.replace(pattern, " ");
        break;
      }
    }
    const words = text.split(/\s+/).filter(word => word && !ignored.has(word)).slice(0, 12);
    const clauses = chosen ? [chosen] : [];
    words.forEach((word, index) => {
      const term = index === words.length - 1 ? `${word}*` : `"${word}"`;
      clauses.push(`(title:${term} OR tag:${term} OR variants.title:${term} OR variants.sku:${term})`);
    });
    return clauses.join(" AND ");
  }
  function normalizeURL(value) {
    const url = new URL(value, window.location.origin);
    if (!/\/search\/?$/.test(url.pathname)) return url;
    const raw = url.searchParams.get("by_q") || decode(url.searchParams.get("q"));
    const handle = collectionHandle(raw);
    if (handle) {
      const preview = url.searchParams.get("preview_theme_id");
      url.pathname = url.pathname.replace(/\/search\/?$/, `/collections/${handle}`);
      url.search = "";
      if (preview) url.searchParams.set("preview_theme_id", preview);
      return url;
    }
    const query = compile(raw);
    if (!query) return url;
    url.searchParams.set("q", query);
    url.searchParams.set("by_q", raw.trim());
    url.searchParams.set("type", "product");
    url.searchParams.set("options[unavailable_products]", "last");
    url.searchParams.set("options[fields]", "title,tag,variants.title,variants.sku");
    url.searchParams.set("options[prefix]", "none");
    return url;
  }
  function buildURL(query, action) {
    const url = new URL(action || window.MinimogSettings?.routes.search_url || "/search", window.location.origin);
    url.searchParams.set("q", query);
    url.searchParams.set("by_q", decode(query));
    const preview = new URL(window.location.href).searchParams.get("preview_theme_id");
    if (preview) url.searchParams.set("preview_theme_id", preview);
    return normalizeURL(url);
  }
  function refresh() {
    const url = new URL(window.location.href);
    if (!/\/search\/?$/.test(url.pathname)) return;
    const display = url.searchParams.get("by_q") || decode(url.searchParams.get("q"));
    document.querySelectorAll(".m-search input[name='q']:not([type='hidden'])").forEach(input => { input.value = display; });
    document.querySelectorAll("#CollectionFiltersForm input[name='by_q']").forEach(input => { input.value = display; });
    document.querySelectorAll("[data-by-search-template]").forEach(node => {
      const template = new DOMParser().parseFromString(node.dataset.bySearchTemplate, "text/html").body.textContent;
      node.textContent = template.replace("__BY_SEARCH_TERM__", display);
    });
    const heading = document.querySelector("[data-by-search-template]");
    if (heading) {
      const suffix = document.title.match(/\s[–-]\s.*$/)?.[0] || "";
      document.title = heading.textContent.trim() + suffix;
    }
    document.documentElement.classList.remove("by-search-initializing");
  }
  window.ByProductSearch = { compile, decode, collectionHandle, normalizeURL, buildURL, refresh };
  if (typeof document === "undefined") return;
  const current = new URL(window.location.href);
  if (/\/search\/?$/.test(current.pathname) && current.searchParams.get("q")) {
    const strict = normalizeURL(current);
    if (strict.href !== current.href) {
      window.location.replace(strict.href);
      return;
    }
  }
  document.addEventListener("submit", event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !/\/search\/?$/.test(new URL(form.action, window.location.href).pathname)) return;
    const input = form.querySelector("input[name='q']:not([type='hidden'])");
    if (!input || form.querySelector("[aria-selected='true'] a")) return;
    event.preventDefault();
    if (compile(input.value)) window.location.assign(buildURL(input.value, form.action).href);
  }, true);
  document.addEventListener("click", event => {
    const anchor = event.target.closest?.("a[href]");
    if (!anchor) return;
    const url = new URL(anchor.href, window.location.href);
    if (url.origin === window.location.origin && /\/search\/?$/.test(url.pathname) && url.searchParams.has("q")) anchor.href = normalizeURL(url).href;
  }, true);
  document.addEventListener("collection:rerendered", refresh);
  window.addEventListener("pageshow", refresh);
  refresh();
})();
